import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Car, Home, Utensils, Backpack, Plane, Plus, Edit, Trash2, 
  Loader2, Check, X, Euro, Camera, MapPin
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

// Helper to get API URL - uses current origin in production
const getApiUrl = () => {
  if (typeof window !== 'undefined' && window.location.origin.includes('xperience-guides.com')) {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || window.location.origin;
};
const API_URL = getApiUrl();

// Service types configuration
const SERVICE_TYPES = {
  vehicle: {
    icon: Car,
    label_fr: 'Véhicule',
    label_en: 'Vehicle',
    color: 'bg-blue-100 text-blue-600',
    subtypes: [
      { value: 'car', label_fr: 'Voiture', label_en: 'Car' },
      { value: 'van', label_fr: 'Van / Camping-car', label_en: 'Van / Camper' },
      { value: '4x4', label_fr: '4x4 / SUV', label_en: '4x4 / SUV' },
      { value: 'motorcycle', label_fr: 'Moto', label_en: 'Motorcycle' },
      { value: 'bike', label_fr: 'Vélo', label_en: 'Bike' },
      { value: 'boat', label_fr: 'Bateau', label_en: 'Boat' },
    ]
  },
  accommodation: {
    icon: Home,
    label_fr: 'Hébergement',
    label_en: 'Accommodation',
    color: 'bg-green-100 text-green-600',
    subtypes: [
      { value: 'room', label_fr: 'Chambre chez l\'habitant', label_en: 'Room at host\'s' },
      { value: 'apartment', label_fr: 'Appartement / Gîte', label_en: 'Apartment / Cottage' },
      { value: 'camping', label_fr: 'Emplacement camping', label_en: 'Camping spot' },
      { value: 'glamping', label_fr: 'Glamping / Insolite', label_en: 'Glamping / Unusual' },
    ]
  },
  equipment: {
    icon: Backpack,
    label_fr: 'Équipement',
    label_en: 'Equipment',
    color: 'bg-orange-100 text-orange-600',
    subtypes: [
      { value: 'ski', label_fr: 'Matériel de ski', label_en: 'Ski equipment' },
      { value: 'diving', label_fr: 'Équipement plongée', label_en: 'Diving gear' },
      { value: 'hiking', label_fr: 'Matériel randonnée', label_en: 'Hiking gear' },
      { value: 'photo', label_fr: 'Matériel photo', label_en: 'Photo equipment' },
      { value: 'climbing', label_fr: 'Équipement escalade', label_en: 'Climbing gear' },
      { value: 'water_sports', label_fr: 'Sports nautiques', label_en: 'Water sports' },
    ]
  },
  meal: {
    icon: Utensils,
    label_fr: 'Repas',
    label_en: 'Meals',
    color: 'bg-yellow-100 text-yellow-600',
    subtypes: [
      { value: 'picnic', label_fr: 'Pique-nique', label_en: 'Picnic' },
      { value: 'lunch', label_fr: 'Déjeuner', label_en: 'Lunch' },
      { value: 'dinner', label_fr: 'Dîner', label_en: 'Dinner' },
      { value: 'full_board', label_fr: 'Pension complète', label_en: 'Full board' },
      { value: 'local_tasting', label_fr: 'Dégustation locale', label_en: 'Local tasting' },
    ]
  },
  transfer: {
    icon: Plane,
    label_fr: 'Transfert',
    label_en: 'Transfer',
    color: 'bg-purple-100 text-purple-600',
    subtypes: [
      { value: 'airport', label_fr: 'Aéroport', label_en: 'Airport' },
      { value: 'train_station', label_fr: 'Gare', label_en: 'Train station' },
      { value: 'custom', label_fr: 'Sur demande', label_en: 'On request' },
    ]
  }
};

const GuideServicesManager = () => {
  const { i18n } = useTranslation();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    service_type: '',
    subtype: '',
    name: '',
    description: '',
    price: 0,
    capacity: '',
    included_details: ''
  });

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const token = localStorage.getItem('vgt_token');
      const response = await fetch(`${API_URL}/api/explorer/services`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setServices(data || []);
      }
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (service = null) => {
    if (service) {
      setEditingService(service);
      setFormData({
        service_type: service.service_type,
        subtype: service.subtype || '',
        name: service.name,
        description: service.description || '',
        price: service.price || 0,
        capacity: service.capacity || '',
        included_details: service.included_details || ''
      });
    } else {
      setEditingService(null);
      setFormData({
        service_type: '',
        subtype: '',
        name: '',
        description: '',
        price: 0,
        capacity: '',
        included_details: ''
      });
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.service_type || !formData.name) {
      toast.error(i18n.language === 'fr' ? 'Veuillez remplir les champs requis' : 'Please fill required fields');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('vgt_token');
      const url = editingService 
        ? `${API_URL}/api/explorer/services/${editingService.service_id}`
        : `${API_URL}/api/explorer/services`;
      
      const response = await fetch(url, {
        method: editingService ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          service_type: formData.service_type,
          name: formData.name,
          description: `${formData.description}${formData.capacity ? `\n[Capacité: ${formData.capacity}]` : ''}${formData.included_details ? `\n[Inclus: ${formData.included_details}]` : ''}`,
          price: parseFloat(formData.price) || 0
        })
      });

      if (response.ok) {
        toast.success(editingService 
          ? (i18n.language === 'fr' ? 'Service modifié !' : 'Service updated!')
          : (i18n.language === 'fr' ? 'Service ajouté !' : 'Service added!')
        );
        setShowModal(false);
        fetchServices();
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      toast.error(i18n.language === 'fr' ? 'Erreur lors de l\'enregistrement' : 'Error saving');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (serviceId) => {
    if (!window.confirm(i18n.language === 'fr' ? 'Supprimer ce service ?' : 'Delete this service?')) return;

    try {
      const token = localStorage.getItem('vgt_token');
      const response = await fetch(`${API_URL}/api/explorer/services/${serviceId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success(i18n.language === 'fr' ? 'Service supprimé' : 'Service deleted');
        fetchServices();
      }
    } catch (error) {
      toast.error(i18n.language === 'fr' ? 'Erreur' : 'Error');
    }
  };

  const getServiceConfig = (type) => SERVICE_TYPES[type] || SERVICE_TYPES.equipment;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">
            {i18n.language === 'fr' ? 'Mes services additionnels' : 'My Additional Services'}
          </h2>
          <p className="text-sm text-muted-foreground">
            {i18n.language === 'fr' 
              ? 'Proposez des services complémentaires à vos expériences'
              : 'Offer complementary services with your experiences'}
          </p>
        </div>
        <Button onClick={() => handleOpenModal()} className="gap-2" data-testid="add-service-btn">
          <Plus className="w-4 h-4" />
          {i18n.language === 'fr' ? 'Ajouter un service' : 'Add Service'}
        </Button>
      </div>

      {/* Services List */}
      {services.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed">
          <Backpack className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="font-medium text-gray-600 mb-2">
            {i18n.language === 'fr' ? 'Aucun service ajouté' : 'No services added'}
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            {i18n.language === 'fr' 
              ? 'Ajoutez des services comme un véhicule, hébergement ou équipement pour enrichir vos offres.'
              : 'Add services like vehicle, accommodation, or equipment to enhance your offerings.'}
          </p>
          <Button variant="outline" onClick={() => handleOpenModal()}>
            <Plus className="w-4 h-4 mr-2" />
            {i18n.language === 'fr' ? 'Ajouter mon premier service' : 'Add my first service'}
          </Button>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          <AnimatePresence>
            {services.map((service) => {
              const config = getServiceConfig(service.service_type);
              const Icon = config.icon;
              
              return (
                <motion.div
                  key={service.service_id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="bg-white rounded-xl border p-4 hover:shadow-md transition-shadow"
                  data-testid={`service-card-${service.service_type}`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${config.color}`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <h3 className="font-semibold">{service.name}</h3>
                          <span className="text-xs text-muted-foreground capitalize">
                            {i18n.language === 'fr' ? config.label_fr : config.label_en}
                          </span>
                        </div>
                        {service.price > 0 && (
                          <span className="text-primary font-bold whitespace-nowrap">
                            {service.price}€
                          </span>
                        )}
                      </div>
                      {service.description && (
                        <p className="text-sm text-gray-600 mt-2 line-clamp-2">
                          {service.description.split('\n')[0]}
                        </p>
                      )}
                      <div className="flex gap-2 mt-3">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleOpenModal(service)}
                        >
                          <Edit className="w-3 h-3 mr-1" />
                          {i18n.language === 'fr' ? 'Modifier' : 'Edit'}
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleDelete(service.service_id)}
                          className="text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}

      {/* Quick Add Buttons */}
      {services.length > 0 && services.length < 5 && (
        <div className="flex flex-wrap gap-2 pt-4 border-t">
          <span className="text-sm text-muted-foreground mr-2 py-1">
            {i18n.language === 'fr' ? 'Ajouter rapidement :' : 'Quick add:'}
          </span>
          {Object.entries(SERVICE_TYPES).map(([type, config]) => {
            const hasType = services.some(s => s.service_type === type);
            if (hasType) return null;
            const Icon = config.icon;
            return (
              <Button
                key={type}
                variant="outline"
                size="sm"
                onClick={() => {
                  setFormData({ ...formData, service_type: type, name: '' });
                  setEditingService(null);
                  setShowModal(true);
                }}
                className="gap-1"
              >
                <Icon className="w-3 h-3" />
                {i18n.language === 'fr' ? config.label_fr : config.label_en}
              </Button>
            );
          })}
        </div>
      )}

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingService 
                ? (i18n.language === 'fr' ? 'Modifier le service' : 'Edit Service')
                : (i18n.language === 'fr' ? 'Ajouter un service' : 'Add Service')}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Service Type */}
            <div>
              <label className="text-sm font-medium mb-2 block">
                {i18n.language === 'fr' ? 'Type de service *' : 'Service Type *'}
              </label>
              <div className="grid grid-cols-5 gap-2">
                {Object.entries(SERVICE_TYPES).map(([type, config]) => {
                  const Icon = config.icon;
                  const isSelected = formData.service_type === type;
                  return (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setFormData({ ...formData, service_type: type })}
                      className={`p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-1 ${
                        isSelected 
                          ? 'border-primary bg-primary/5' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <Icon className={`w-5 h-5 ${isSelected ? 'text-primary' : 'text-gray-500'}`} />
                      <span className={`text-xs ${isSelected ? 'text-primary font-medium' : 'text-gray-500'}`}>
                        {i18n.language === 'fr' ? config.label_fr : config.label_en}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Name */}
            <div>
              <label className="text-sm font-medium mb-2 block">
                {i18n.language === 'fr' ? 'Nom du service *' : 'Service Name *'}
              </label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={i18n.language === 'fr' ? 'Ex: Van Mercedes équipé 4 places' : 'Ex: Equipped 4-seat Mercedes Van'}
              />
            </div>

            {/* Description */}
            <div>
              <label className="text-sm font-medium mb-2 block">
                {i18n.language === 'fr' ? 'Description' : 'Description'}
              </label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={i18n.language === 'fr' 
                  ? 'Décrivez votre service en détail...'
                  : 'Describe your service in detail...'}
                rows={3}
              />
            </div>

            {/* Capacity */}
            <div>
              <label className="text-sm font-medium mb-2 block">
                {i18n.language === 'fr' ? 'Capacité (optionnel)' : 'Capacity (optional)'}
              </label>
              <Input
                value={formData.capacity}
                onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                placeholder={i18n.language === 'fr' ? 'Ex: 4 personnes, 2 chambres...' : 'Ex: 4 people, 2 bedrooms...'}
              />
            </div>

            {/* Price */}
            <div>
              <label className="text-sm font-medium mb-2 block">
                {i18n.language === 'fr' ? 'Prix (€) - 0 = sur demande' : 'Price (€) - 0 = on request'}
              </label>
              <div className="relative">
                <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="number"
                  min="0"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  className="pl-10"
                  placeholder="0"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {i18n.language === 'fr' 
                  ? 'Laissez 0 pour indiquer "sur demande" ou "inclus"'
                  : 'Leave 0 for "on request" or "included"'}
              </p>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowModal(false)}>
                {i18n.language === 'fr' ? 'Annuler' : 'Cancel'}
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                {i18n.language === 'fr' ? 'Enregistrer' : 'Save'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GuideServicesManager;
